import ReactECharts from 'echarts-for-react';
import { useMemo, useState } from 'react';
import type { CustomSeriesRenderItemAPI, CustomSeriesRenderItemParams } from 'echarts';
import { t } from 'utilities/TranslationUtils';
import type { CornerZoneLayoutDto, CornerSide, MeasurementMarkerDto } from 'types/models/SessionsPageData';
import { buildZoneStats, getCornerZoneIdForLayout } from './playerPerformanceUtils';
import { buildCornerZoneOption } from './cornerZonePitchOptions';
import { GOOD_SCORE_COLOR, MEDIUM_SCORE_COLOR, POOR_SCORE_COLOR } from './cornerZoneScoreColor';
import './CornerZonePitch.scss';

interface Props {
  layouts: CornerZoneLayoutDto[];
  measurements: MeasurementMarkerDto[];
  selectedLayoutId: string;
  selectedSide: CornerSide;
  selectedZones: Set<number>;
  onSelectSide: (side: CornerSide) => void;
  onToggleZone: (zoneId: number) => void;
  onClearZones: () => void;
}

/**
 * Renders a single `custom` zone-polygon series with enhanced styling (dotted lines, rounded corners).
 * ECharts' `renderItem` runs per-series, so the `polygon` (chart-space points, pre-computed by the 
 * pure option builder) and `itemStyle` are read straight off the series definition.
 */
function renderZonePolygon(
  polygon: [number, number][],
  stat: { kickCount: number; onTargetPct: number | null },
  selectionState: { selectionActive: boolean; isSelected: boolean },
  itemStyle: { 
    color?: string
    opacity?: number
    borderColor?: string
    borderWidth?: number
    zoneDotted?: boolean
    zoneCornerRadius?: number
  },
) {
  return (params: CustomSeriesRenderItemParams, api: CustomSeriesRenderItemAPI) => {
    const points = polygon.map(([z, x]) => api.coord([z, x]));
    const kickCount = stat.kickCount;
    const onTargetPct = stat.onTargetPct;
    const isHovered = (params as any).state === 'emphasis';
    const showLabels = !selectionState.selectionActive || selectionState.isSelected || isHovered;
    const labelOpacity = showLabels ? 1 : 0.34;

    // Compute a stable geometric center from the polygon bounds.
    // We intentionally avoid a raw average of vertices because many polygons
    // include a duplicated closing point, which visually biases text alignment.
    const uniquePoints = points.filter((pt, idx) => {
      if (idx === 0) return true;
      const prev = points[idx - 1];
      return pt[0] !== prev[0] || pt[1] !== prev[1];
    });
    const xs = uniquePoints.map((pt) => pt[0]);
    const ys = uniquePoints.map((pt) => pt[1]);
    const minX = Math.min(...xs);
    const maxX = Math.max(...xs);
    const minY = Math.min(...ys);
    const maxY = Math.max(...ys);
    const cx = (minX + maxX) / 2;
    const cy = (minY + maxY) / 2;

    const percentageText = kickCount === 0 ? '-' : `${Math.round(onTargetPct ?? 0)}%`;
    const kicksText = kickCount === 0 ? '-' : `${kickCount} ${kickCount === 1 ? 'kick' : 'kicks'}`;

    // Auto-fit label block inside the zone bounds with safe side/top/bottom margins.
    const horizontalMargin = 10;
    const verticalMargin = 8;
    const availableWidth = Math.max(8, (maxX - minX) - horizontalMargin * 2);
    const availableHeight = Math.max(8, (maxY - minY) - verticalMargin * 2);

    const basePctFont = 24;
    const baseSubFont = 12;
    const baseGap = 10;

    // Conservative width estimates (slightly over-estimate to avoid overflow).
    const estimateTextWidth = (text: string, fontSize: number) => {
      const length = Math.max(1, text.length);
      return length * fontSize * 0.64;
    };

    const pctWidthBase = estimateTextWidth(percentageText, basePctFont);
    const subWidthBase = estimateTextWidth(kicksText, baseSubFont);
    const widthScale = Math.min(1, availableWidth / Math.max(pctWidthBase, subWidthBase));

    const baseBlockHeight = basePctFont + baseGap + baseSubFont;
    const heightScale = Math.min(1, availableHeight / baseBlockHeight);

    const scale = Math.max(0.34, Math.min(widthScale, heightScale));
    const pctFontSize = Math.max(10, Math.round(basePctFont * scale));
    const subFontSize = Math.max(8, Math.round(baseSubFont * scale));
    const gap = Math.max(4, Math.round(baseGap * scale));

    const blockHeight = pctFontSize + gap + subFontSize;
    const blockTop = cy - blockHeight / 2;
    const percentageY = blockTop + pctFontSize / 2;
    const subtitleY = blockTop + pctFontSize + gap + subFontSize / 2;

    // Optical correction: heavy glyphs like '%' can appear slightly right-shifted.
    // A tiny left nudge keeps labels visually centered in the zone.
    const opticalCenterX = cx - 1;

    // Build line style with optional dotted pattern
    const lineStyle: any = {
      stroke: itemStyle.borderColor,
      lineWidth: itemStyle.borderWidth,
    };
    if (itemStyle.zoneDotted) {
      lineStyle.lineDash = [4, 4]; // Dotted line pattern
    }

    const baseOpacity = itemStyle.opacity ?? 0.6;
    const boostedOpacity = Math.min(0.95, baseOpacity + 0.16);

    return {
      type: 'group' as const,
      children: [
        {
          type: 'polygon' as const,
          shape: { points, r: itemStyle.zoneCornerRadius ?? 0 },
          style: {
            fill: itemStyle.color,
            opacity: showLabels ? boostedOpacity : baseOpacity,
            ...lineStyle,
          },
        },
        // Percentage drop shadow
        {
          type: 'text' as const,
          style: {
            text: percentageText,
            x: opticalCenterX + 1,
            y: percentageY + 2,
            textAlign: 'center',
            textVerticalAlign: 'middle',
            font: `700 ${pctFontSize}px Inter, sans-serif`,
            fill: 'rgba(0,0,0,0.5)',
            opacity: labelOpacity,
          },
          silent: true,
        },
        // Percentage text
        {
          type: 'text' as const,
          style: {
            text: percentageText,
            x: opticalCenterX,
            y: percentageY,
            textAlign: 'center',
            textVerticalAlign: 'middle',
            font: `700 ${pctFontSize}px Inter, sans-serif`,
            fill: '#ffffff',
            opacity: labelOpacity,
          },
          silent: true,
        },
        // Kicks line drop shadow
        {
          type: 'text' as const,
          style: {
            text: kicksText,
            x: opticalCenterX + 1,
            y: subtitleY + 2,
            textAlign: 'center',
            textVerticalAlign: 'middle',
            font: `600 ${subFontSize}px Inter, sans-serif`,
            fill: 'rgba(0,0,0,0.5)',
            opacity: labelOpacity,
          },
          silent: true,
        },
        // Kicks line text
        {
          type: 'text' as const,
          style: {
            text: kicksText,
            x: opticalCenterX,
            y: subtitleY,
            textAlign: 'center',
            textVerticalAlign: 'middle',
            font: `600 ${subFontSize}px Inter, sans-serif`,
            fill: '#ffffff',
            opacity: labelOpacity,
          },
          silent: true,
        },
      ],
    };
  };
}

/**
 * Renders a corner flag (pole + triangular flag) with optional selection highlight.
 * Position is passed as [chartZ, chartX]; returns a composite flag shape.
 * Only selected flag has orange fill; unselected flags are transparent/white.
 * Hover changes flag opacity to halfway between selected and unselected.
 */
function renderFlag(
  position: [number, number],
  isSelected: boolean,
  itemStyle: { color?: string; selectedColor?: string }
) {
  return (params: CustomSeriesRenderItemParams, api: CustomSeriesRenderItemAPI) => {
    const [z, x] = position;
    const [px, py] = api.coord([z, x]);

    // Flag dimensions (larger for better visibility and tappability)
    const poleWidth = 3;
    const poleHeight = 35;
    const flagWidth = 20;
    const flagHeight = 15;

    // Detect hover state from ECharts params (cast to any for runtime access to state property)
    const isHovering = (params as any).state === 'emphasis';

    // Interpolate flag color based on selection and hover state:
    // - Selected: orange (#ff9800)
    // - Hovering (any): halfway opacity between selected and unselected
    // - Unselected: transparent white (low opacity)
    let flagColor: string;
    if (isSelected) {
      flagColor = isHovering ? 'rgba(255, 152, 0, 0.65)' : '#ff9800';
    } else {
      flagColor = isHovering ? 'rgba(255, 255, 255, 0.65)' : 'rgba(255, 255, 255, 0.18)';
    }

    // Group element containing pole + flag + invisible hitbox
    return {
      type: 'group' as const,
      children: [
        // Invisible hitbox circle (easier to tap/click, no visual indicator)
        {
          type: 'circle' as const,
          shape: { cx: 0, cy: 0, r: 30 },
          position: [px, py - poleHeight / 2],
          style: {
            fill: 'rgba(0,0,0,0)',
            stroke: 'rgba(0,0,0,0)',
          },
        },
        // Vertical pole
        {
          type: 'rect' as const,
          shape: {
            x: px - poleWidth / 2,
            y: py - poleHeight,
            width: poleWidth,
            height: poleHeight,
          },
          style: {
            fill: '#ffffff',
            stroke: '#333333',
            lineWidth: 1,
          },
        },
        // Triangular flag (drawn as polygon)
        {
          type: 'polygon' as const,
          shape: {
            points: [
              [px, py - poleHeight],                    // Left edge at pole top
              [px + flagWidth, py - poleHeight + 5],    // Right point (tip)
              [px, py - poleHeight + flagHeight],       // Bottom edge
            ],
          },
          style: {
            fill: flagColor,
            stroke: '#ffffff',
            lineWidth: isSelected ? 3 : 2,
          },
        },
      ],
    };
  };
}

export default function CornerZonePitch({
  layouts,
  measurements,
  selectedLayoutId,
  selectedSide,
  selectedZones,
  onSelectSide,
  onToggleZone,
  onClearZones,
}: Props) {
  // Filter to active side only and count kicks per side
  const sideMeasurements = useMemo(
    () => measurements.filter((m) => {
      const kickZ = m.kickZ ?? 0;
      const side = kickZ > 0 ? 'Left' : 'Right';
      return side === selectedSide;
    }),
    [measurements, selectedSide],
  );

  // Kicks in the active side that have no zone assignment for the selected layout.
  // These are excluded from the zone stats and the user should know.
  const kicksWithoutZoneData = useMemo(
    () => sideMeasurements.filter((m) => getCornerZoneIdForLayout(m.cornerZones ?? undefined, selectedLayoutId) == null).length,
    [sideMeasurements, selectedLayoutId],
  );

  // Get active layout zones
  const activeZones = useMemo(
    () => layouts.find((l) => l.layoutId === selectedLayoutId && l.side === selectedSide)?.zones ?? [],
    [layouts, selectedLayoutId, selectedSide],
  );

  // Per-zone stats via the shared builder, keyed by the layout-specific corner zone id. Using
  // buildZoneStats (same on-target classifier as the free-kick pitch and the summary tile) keeps the
  // pitch cell % and the tile % identical for the same kicks — never bespoke math here.
  const stats = useMemo(
    () => buildZoneStats(sideMeasurements, (m) => getCornerZoneIdForLayout(m.cornerZones ?? undefined, selectedLayoutId)),
    [sideMeasurements, selectedLayoutId],
  );

  // Build chart option
  const option = useMemo(
    () => buildCornerZoneOption({ zones: activeZones, stats, kicks: [], selected: selectedZones, side: selectedSide }),
    [activeZones, stats, selectedZones, selectedSide],
  );

  const seriesWithRender = useMemo(() => (option.series as any[]).map((s) => {
    if (s.type !== 'custom') return s;
    
    // Handle zone polygons
    if (s.id?.startsWith('corner-zone-')) {
      const zoneId = Number(String(s.id).replace('corner-zone-', ''));
      const zoneData = (s.data?.[0] ?? {}) as { kickCount?: number; onTargetPct?: number | null };
      const zoneStat = {
        kickCount: zoneData.kickCount ?? 0,
        onTargetPct: zoneData.onTargetPct ?? null,
      };
      const selectionState = {
        selectionActive: selectedZones.size > 0,
        isSelected: selectedZones.has(zoneId),
      };
      return { ...s, renderItem: renderZonePolygon(s.polygon ?? [], zoneStat, selectionState, s.itemStyle ?? {}) };
    }
    
    // Handle corner flags
    if (s.id === 'corner-flag-left') {
      const isSelected = selectedSide === 'Left';
      return { ...s, renderItem: renderFlag(s.flagPosition ?? [0, 0], isSelected, s.itemStyle ?? {}) };
    }
    if (s.id === 'corner-flag-right') {
      const isSelected = selectedSide === 'Right';
      return { ...s, renderItem: renderFlag(s.flagPosition ?? [0, 0], isSelected, s.itemStyle ?? {}) };
    }
    
    return s;
  }), [option, selectedSide]);

  const onEvents = useMemo(() => ({
    click: (p: any) => {
      // Handle flag clicks to change side
      if (p.seriesId === 'corner-flag-left') {
        onSelectSide('Left');
        return;
      }
      if (p.seriesId === 'corner-flag-right') {
        onSelectSide('Right');
        return;
      }
      // Handle zone clicks
      const id = typeof p.seriesId === 'string' && p.seriesId.startsWith('corner-zone-')
        ? Number(p.seriesId.replace('corner-zone-', '')) : null;
      if (id == null || Number.isNaN(id)) return;
      // Tapping an empty zone resets current zone selection.
      if (!stats.get(id)?.kickCount) {
        onClearZones();
        return;
      }
      onToggleZone(id);
    },
  }), [onClearZones, onToggleZone, onSelectSide, stats]);

  return (
    <div className='pp-section corner-zone-pitch' data-test='corner-zone-pitch'>
      <div className='corner-zone-pitch__chart'>
        <div className='corner-zone-pitch__legend' aria-hidden='true'>
          <div className='corner-zone-pitch__legend-item'>
            <span className='corner-zone-pitch__legend-swatch' style={{ backgroundColor: POOR_SCORE_COLOR }} />
            <span>&lt;20 %</span>
          </div>
          <div className='corner-zone-pitch__legend-item'>
            <span className='corner-zone-pitch__legend-swatch' style={{ backgroundColor: MEDIUM_SCORE_COLOR }} />
            <span>21-50 %</span>
          </div>
          <div className='corner-zone-pitch__legend-item'>
            <span className='corner-zone-pitch__legend-swatch' style={{ backgroundColor: GOOD_SCORE_COLOR }} />
            <span>&gt;51 %</span>
          </div>
        </div>
        <ReactECharts
          option={{ ...option, series: seriesWithRender }}
          onEvents={onEvents}
          style={{ width: '100%', height: '100%' }}
          notMerge
        />
      </div>
      {kicksWithoutZoneData > 0 && (
        <p className='corner-zone-pitch__subtitle'>
          {t('playerPerformance.cornerZoneMissingZoneData', { count: kicksWithoutZoneData })}
        </p>
      )}
    </div>
  );
}
